"use client";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IoMdMenu } from "react-icons/io";
import { MdClose } from "react-icons/md";
import MenuItems from "./MenuItems";
import ThemeToggle from "./ThemeToggle";
import { FALLBACK_AVATAR, onAvatarError, useAuthUser } from "./useAuthUser";
import { RootState } from "../../../feature/store/store";
import { setIsMenuOpen } from "../../../feature/reducers/appSlice";
import { BOOKING_URL } from "./Desktopnav";

const MobileNav = () => {
    const dispatch = useDispatch();
    const pathname = usePathname();
    const { isMenuOpen } = useSelector((state: RootState) => state.app);
    const { user, logout } = useAuthUser();

    const menuItems = MenuItems();
    const pageLinks = menuItems.filter((item) => !item.legal);
    const legalLinks = menuItems.filter((item) => item.legal);

    const closeMenu = () => dispatch(setIsMenuOpen(false));
    const isActive = (url: string) =>
        url === "/" ? pathname === "/" : pathname?.startsWith(url);

    // Menü schließen, wenn die Seite wechselt
    useEffect(() => {
        dispatch(setIsMenuOpen(false));
    }, [pathname, dispatch]);

    // Escape schließt; Seite dahinter scrollt nicht, solange das Menü offen ist
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") dispatch(setIsMenuOpen(false));
        };
        window.addEventListener("keydown", onKey);
        document.body.style.overflow = isMenuOpen ? "hidden" : "";
        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = "";
        };
    }, [isMenuOpen, dispatch]);

    return (
        <>
            {/* Leiste oben – wie im Entwurf: Logo links, Menü-Knopf rechts */}
            <header className="sticky top-0 z-40 border-b border-ao-line bg-ao-bg/95 backdrop-blur-md lg:hidden">
                <nav aria-label="Hauptnavigation" className="flex h-16 items-center justify-between px-5">
                    <Link href="/" className="flex items-center gap-2.5">
                        <img
                            src="/logo.png"
                            alt="A und O Autoservice"
                            className="h-[34px] w-[34px] rounded-[9px] object-cover"
                        />
                        <span className="font-display text-[19px] font-extrabold tracking-[0.04em] text-ao-fg">
                            A UND O
                        </span>
                    </Link>
                    <button
                        type="button"
                        aria-label="Menü öffnen"
                        aria-expanded={isMenuOpen}
                        onClick={() => dispatch(setIsMenuOpen(true))}
                        className="flex h-11 w-11 items-center justify-center rounded-[10px] border border-ao-line-strong text-ao-fg"
                    >
                        <IoMdMenu className="text-[22px]" />
                    </button>
                </nav>
            </header>

            {/* Abdunklung – außerhalb des Headers, sonst verrutscht das Menü */}
            <div
                aria-hidden="true"
                onClick={closeMenu}
                className={`fixed inset-0 z-50 bg-black/60 backdrop-blur-sm transition-opacity duration-300 lg:hidden ${
                    isMenuOpen ? "opacity-100" : "pointer-events-none opacity-0"
                }`}
            />

            {/* Ausfahrendes Menü */}
            <aside
                aria-label="Menü"
                className={`fixed right-0 top-0 z-50 flex h-dvh w-[88%] max-w-sm flex-col overflow-y-auto border-l border-ao-line bg-ao-bg text-ao-fg shadow-2xl transition-transform duration-300 ease-out lg:hidden ${
                    isMenuOpen ? "translate-x-0" : "invisible translate-x-full"
                }`}
            >
                <div className="flex h-16 shrink-0 items-center justify-between border-b border-ao-line px-5">
                    <span className="font-display text-lg font-extrabold tracking-[0.04em]">MENÜ</span>
                    <button
                        type="button"
                        aria-label="Menü schließen"
                        onClick={closeMenu}
                        className="flex h-11 w-11 items-center justify-center rounded-[10px] border border-ao-line-strong"
                    >
                        <MdClose className="text-2xl" />
                    </button>
                </div>

                {/* Konto */}
                <div className="border-b border-ao-line p-5">
                    {user ? (
                        <div className="flex flex-col gap-4">
                            <div className="flex items-center gap-3">
                                <img
                                    src={user.profile_photo || FALLBACK_AVATAR}
                                    onError={onAvatarError}
                                    alt=""
                                    className="h-11 w-11 rounded-full object-cover"
                                />
                                <div className="min-w-0">
                                    <p className="truncate font-semibold">{user.firstName} {user.lastName}</p>
                                    <p className="truncate text-sm text-ao-muted">{user.email}</p>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2.5">
                                <Link
                                    href="/meinProfile"
                                    onClick={closeMenu}
                                    className="flex h-11 items-center justify-center rounded-[10px] border border-ao-line-strong text-sm font-semibold"
                                >
                                    Mein Profil
                                </Link>
                                <button
                                    type="button"
                                    onClick={() => {
                                        closeMenu();
                                        logout();
                                    }}
                                    className="flex h-11 items-center justify-center rounded-[10px] border border-ao-line-strong text-sm font-semibold"
                                >
                                    Abmelden
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 gap-2.5">
                            <Link
                                href="/login"
                                onClick={closeMenu}
                                className="flex h-11 items-center justify-center rounded-[10px] border border-ao-line-strong text-sm font-semibold"
                            >
                                Anmelden
                            </Link>
                            <Link
                                href="/register"
                                onClick={closeMenu}
                                className="flex h-11 items-center justify-center rounded-[10px] border border-ao-line-strong text-sm font-semibold"
                            >
                                Registrieren
                            </Link>
                        </div>
                    )}
                </div>

                {/* Seiten */}
                <ul className="flex flex-col px-5 py-2">
                    {pageLinks.map((item) => (
                        <li key={item.url}>
                            <Link
                                href={item.url}
                                onClick={closeMenu}
                                aria-current={isActive(item.url) ? "page" : undefined}
                                className={`flex min-h-[60px] items-center justify-between border-b border-ao-line font-display text-2xl font-bold uppercase transition-colors ${
                                    isActive(item.url) ? "text-ao-accent-fg" : "text-ao-fg"
                                }`}
                            >
                                {item.title}
                                <span aria-hidden="true" className="text-ao-accent-fg">→</span>
                            </Link>
                        </li>
                    ))}
                </ul>

                {/* Unten: Darstellung, Termin, Rechtliches */}
                <div className="mt-auto flex flex-col gap-5 border-t border-ao-line p-5">
                    <div className="flex flex-col gap-2">
                        <span className="text-[13px] font-semibold text-ao-muted">Darstellung</span>
                        <ThemeToggle withLabels />
                    </div>
                    <Link
                        href={BOOKING_URL}
                        onClick={closeMenu}
                        className="flex h-[54px] items-center justify-center rounded-xl bg-ao-accent text-base font-bold text-ao-on-accent"
                    >
                        Termin online buchen
                    </Link>
                    <div className="flex gap-6 text-sm text-ao-muted">
                        {legalLinks.map((item) => (
                            <Link key={item.url} href={item.url} onClick={closeMenu}>
                                {item.title}
                            </Link>
                        ))}
                    </div>
                </div>
            </aside>
        </>
    );
};

export default MobileNav;
